import {
  getProfile,
  initializeAuth,
  isAuthenticated,
  login,
  logout,
  signOutAndReload,
} from "@/lib/auth";
import { fetchCurrentUser } from "@/lib/api/me-api";
import { getApiAdapter } from "@/lib/api/get-adapter";

const api = () => getApiAdapter();

export const authApi = {
  login,
  logout,
  signOutAndReload,
  isAuthenticated,
  getProfile,
  initializeAuth,
  fetchCurrentUser,
};

export { navigateAfterSamlLogin as navigateAfterMicrosoftLogin } from "@/lib/auth";
export { fetchHealth, fetchServicesHealth } from "@/lib/api/health-api";
export type {
  HealthCheckResult,
  ServiceHealthItem,
  ServicesHealthData,
  ServicesHealthResult,
} from "@/lib/api/health-api";

export const usersApi = {
  list: (params?: Parameters<ReturnType<typeof getApiAdapter>["users"]["list"]>[0]) =>
    api().users.list(params ?? {}),
};

export const rolesApi = {
  list: () => api().roles.list(),
  getById: (id: string) => api().roles.getById(id),
  updatePermissions: (id: string, permissionIds: string[]) =>
    api().roles.updatePermissions(id, permissionIds),
};

export const permissionsApi = {
  list: () => api().permissions.list(),
};

export const alertsApi = {
  list: (params?: Parameters<ReturnType<typeof getApiAdapter>["alerts"]["list"]>[0]) =>
    api().alerts.list(params),
  updateStatus: (
    id: number,
    action: Parameters<ReturnType<typeof getApiAdapter>["alerts"]["updateStatus"]>[1],
  ) => api().alerts.updateStatus(id, action),
};

export const videosApi = {
  list: () => api().videos.list(),
  getById: (id: number) => api().videos.getById(id),
};

export const screenshotsApi = {
  list: (params?: Parameters<ReturnType<typeof getApiAdapter>["screenshots"]["list"]>[0]) =>
    api().screenshots.list(params),
};

export const auditTrailApi = {
  list: () => api().auditTrail.list(),
  getDetail: (videoName: string) => api().auditTrail.getDetail(videoName),
};

export const historyApi = {
  list: () => api().history.list(),
};

export {
  fetchVideoHistory,
  fetchHistoryMasterOptions,
  getVideoHistoryTotalPages,
} from "@/lib/api/videos-history-api";
export type { VideoHistoryItem } from "@/lib/api/videos-history-api";

export {
  fetchVideoScreenshots,
  getVideoScreenshotsTotalPages,
} from "@/lib/api/video-screenshots-api";
export type { VideoScreenshotItem } from "@/lib/api/video-screenshots-api";

export {
  fetchVideoAlerts,
  fetchVideoAlertChartData,
  fetchFrameComments,
  createFrameComment,
  updateFrameComment,
  deleteFrameComment,
  getVideoAlertsTotalPages,
  updateVideoAlertStatus,
} from "@/lib/api/video-alerts-api";
export type { VideoAlertItem } from "@/types/video-alert-types";

export const dashboardApi = {
  getStats: (
    period: Parameters<ReturnType<typeof getApiAdapter>["dashboard"]["getStats"]>[0],
  ) => api().dashboard.getStats(period),
  // getActivity: (
  //   period: Parameters<ReturnType<typeof getApiAdapter>["dashboard"]["getActivity"]>[0],
  // ) => api().dashboard.getActivity(period),
};

export const uploadsApi = {
  uploadVideos: (files: File[]) => api().uploads.uploadVideos(files),
};

export {
  confirmVideoUpload,
  fetchVideoSignedUrl,
  uploadVideoToS3,
} from "@/lib/api/videos-signed-url-api";

export {
  createEquipment,
  createIntervention,
  createInterventionCategory,
  createMaster,
  deleteEquipment,
  deleteIntervention,
  deleteInterventionCategory,
  deleteMaster,
  fetchEquipment,
  fetchEquipmentByMaster,
  fetchIntervention,
  fetchInterventionCategories,
  fetchInterventionCategory,
  fetchInterventions,
  fetchMaster,
  fetchMasters,
  getHierarchyTotalPages,
  getHierarchyErrorMessage,
  updateEquipment,
  updateIntervention,
  updateInterventionCategory,
  updateMaster,
} from "@/lib/api/hierarchy-api";
export type { HierarchyMutationResult } from "@/lib/api/hierarchy-api";

export { getApiConfig } from "@/lib/api/config";
export { clearClientAuthState, clearAppTokens } from "@/lib/api/auth-token";
export {
  ApiError,
  toApiError,
  getUserErrorMessage,
  getDefaultErrorTitle,
  sanitizeUserMessage,
  formatUserErrorMessage,
  apiErrorFromEnvelope,
  type ErrorContext,
} from "@/lib/api/errors";
